import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Toaster } from "sonner";
import { Nav } from "@/components/landing/Nav";
import { Footer } from "@/components/landing/Footer";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useSite } from "@/context/SiteContext";
import { LeadModalProvider } from "@/context/LeadModalContext";
import { API_BASE as API } from "@/lib/api";
import { BRAND_NAME, SITE_NAME } from "@/constants/site";
import { DEFAULT_CURRENCY, formatMoney } from "@/lib/currency";
import { submitBoth, submitSmartInquiry } from "@/lib/inquiry";

const SESSION_KEY = "ss_pricing_session";
function getSessionId() {
  if (typeof window === "undefined") return "";
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `ss-${Math.random().toString(36).slice(2)}-${Date.now()}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

const num = (...values) => {
  for (const v of values) {
    if (v !== null && v !== undefined && v !== "" && Number.isFinite(Number(v))) return Number(v);
  }
  return 0;
};

// Rates shown in the UI: prefer the server's latest breakdown, fall back to CMS settings.
function ratesFrom(breakdown, settings) {
  const a = breakdown?.assumptions || {};
  return {
    currency: breakdown?.currency || settings?.pricing_currency_code || DEFAULT_CURRENCY,
    setSize: Math.max(1, num(a.server_set_size, settings?.pricing_server_set_size, 3)),
    serverMonthly: num(a.server_monthly_per_set, settings?.pricing_server_monthly_per_set),
    installPerSet: num(a.camera_install_per_set, settings?.pricing_camera_install_per_set),
    featureMonthly: num(a.feature_monthly, settings?.pricing_feature_monthly),
  };
}

export default function PricingPage() {
  const { categories, settings } = useSite();

  return (
    <LeadModalProvider categories={categories}>
      <div className="min-h-screen bg-white text-neutral-900" data-testid="pricing-root">
        <Nav categories={categories} />
        <main>
          <Hero />
          <Calculator settings={settings} />
          <Disclaimer settings={settings} />
        </main>
        <Footer categories={categories} />
        <Toaster position="bottom-right" richColors closeButton />
      </div>
    </LeadModalProvider>
  );
}

const Disclaimer = ({ settings }) => {
  const text =
    settings?.pricing_disclaimer ||
    "Pricing shown is tentative and indicative only. Final quote depends on site survey, deployment distance, integrations, network, SLA and support - please reach out for an actual proposal.";
  return (
    <section data-testid="pricing-disclaimer" className="pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-dashed border-amber-300 bg-amber-50 px-5 py-4 flex gap-4 items-start">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex-shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </span>
          <div className="min-w-0">
            <p className="font-mono-ui text-[10px] uppercase tracking-[0.22em] text-amber-700">
              Disclaimer
            </p>
            <p className="mt-1 text-sm text-amber-900 leading-relaxed whitespace-pre-line">
              {text}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const Hero = () => (
  <section
    id="top"
    className="relative pt-12 sm:pt-16 pb-10 overflow-hidden"
  >
    <div className="absolute inset-0 -z-10 bg-grid bg-grid-fade" />
    <div
      aria-hidden
      className="absolute -top-32 -left-32 w-[420px] h-[420px] -z-10 rounded-full blur-3xl opacity-40"
      style={{ background: "radial-gradient(circle, rgba(114,58,186,0.35), transparent 60%)" }}
    />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <span className="font-mono-ui text-[11px] uppercase tracking-[0.22em] text-neutral-500">
        Pricing
      </span>
      <h1
        data-testid="pricing-hero-title"
        className="font-display mt-3 text-[40px] sm:text-6xl font-black tracking-tight leading-[1] text-neutral-950 max-w-3xl"
      >
        Estimate your{" "}
        <span className="relative inline-block">
          <span className="relative z-10">{SITE_NAME}</span>
          <span
            aria-hidden
            className="absolute inset-x-0 bottom-1 h-3 -z-0"
            style={{ background: "rgba(0,169,206,0.35)" }}
          />
        </span>{" "}
        deployment.
      </h1>
      <p className="mt-5 text-base sm:text-lg text-neutral-600 max-w-2xl leading-relaxed">
        A live, transparent calculator. Tell us how many cameras and features you need -
        we&apos;ll show the monthly run-rate and any one-time install cost.
      </p>
    </div>
  </section>
);

const initialState = {
  cameras: 6,
  features: 3,
  has_existing_cameras: true,
  has_gpu_server: false,
};

const Calculator = ({ settings }) => {
  const [state, setState] = useState(initialState);
  const [breakdown, setBreakdown] = useState(null);
  const debounceRef = useRef(null);
  const sessionId = useMemo(() => getSessionId(), []);
  const rates = ratesFrom(breakdown, settings);
  const money = (n) => formatMoney(n, rates.currency);

  // Recompute + log on changes (debounced)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API}/pricing/calculate`, {
          ...state,
          session_id: sessionId,
        });
        setBreakdown(data.breakdown);
      } catch {
        // Silent - calculator should never block UX
      }
    }, 900);
    return () => clearTimeout(debounceRef.current);
  }, [state, sessionId]);

  const update = (k, v) => setState((s) => ({ ...s, [k]: v }));

  return (
    <section className="pb-24" data-testid="pricing-calculator">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-8">
          <motion.div
            className="lg:col-span-7 bg-white border border-neutral-200 rounded-2xl p-6 sm:p-8 space-y-7"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <Stepper
              label="How many cameras do you want to integrate?"
              hint={`Each set of ${rates.setSize} camera${rates.setSize === 1 ? "" : "s"} shares one inference server.`}
              value={state.cameras}
              min={0}
              max={1000}
              onChange={(v) => update("cameras", v)}
              testid="cameras"
            />
            <YesNo
              label="Do you have existing cameras?"
              hint="If no, we add a one-time camera install cost."
              value={state.has_existing_cameras}
              onChange={(v) => update("has_existing_cameras", v)}
              testid="existing-cameras"
            />
            <YesNo
              label="Do you have servers with GPU?"
              hint="If yes, we'll use your hardware - the monthly server cost is waived."
              value={state.has_gpu_server}
              onChange={(v) => update("has_gpu_server", v)}
              testid="gpu-server"
            />
            <Stepper
              label="How many features do you want?"
              hint="ANPR, PPE, vault intrusion etc. Each is a separate trained module."
              value={state.features}
              min={0}
              max={50}
              onChange={(v) => update("features", v)}
              testid="features"
            />
          </motion.div>

          <motion.aside
            className="lg:col-span-5"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
          >
            <Breakdown breakdown={breakdown} state={state} money={money} rates={rates} />
          </motion.aside>
        </div>

        <Inquiry state={state} sessionId={sessionId} money={money} />
      </div>
    </section>
  );
};

const Stepper = ({ label, hint, value, min = 0, max = 9999, onChange, testid }) => {
  const dec = () => onChange(Math.max(min, (value || 0) - 1));
  const inc = () => onChange(Math.min(max, (value || 0) + 1));
  return (
    <div data-testid={`stepper-${testid}`}>
      <label className="block font-display text-base sm:text-lg font-bold text-neutral-950">
        {label}
      </label>
      {hint && <p className="mt-1 text-xs text-neutral-500">{hint}</p>}
      <div className="mt-3 inline-flex items-stretch h-12 rounded-lg border-2 border-neutral-200 overflow-hidden bg-white">
        <button
          type="button"
          data-testid={`stepper-${testid}-decrement`}
          onClick={dec}
          disabled={value <= min}
          className="w-12 flex items-center justify-center text-neutral-700 hover:bg-neutral-50 disabled:text-neutral-300 disabled:cursor-not-allowed transition-colors"
          aria-label="Decrease"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
        <input
          type="number"
          data-testid={`stepper-${testid}-input`}
          value={value}
          min={min}
          max={max}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") {
              onChange(0);
              return;
            }
            const num = Number(raw);
            if (Number.isFinite(num)) onChange(Math.max(min, Math.min(max, Math.round(num))));
          }}
          className="w-24 text-center font-display text-2xl font-black tracking-tight outline-none bg-transparent border-x-2 border-neutral-200"
        />
        <button
          type="button"
          data-testid={`stepper-${testid}-increment`}
          onClick={inc}
          disabled={value >= max}
          className="w-12 flex items-center justify-center text-neutral-700 hover:bg-brand hover:text-white disabled:text-neutral-300 disabled:cursor-not-allowed transition-colors"
          aria-label="Increase"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const YesNo = ({ label, hint, value, onChange, testid }) => (
  <div data-testid={`yesno-${testid}`}>
    <label className="block font-display text-base sm:text-lg font-bold text-neutral-950">{label}</label>
    {hint && <p className="mt-1 text-xs text-neutral-500">{hint}</p>}
    <div className="mt-3 inline-flex bg-neutral-100 p-1 rounded-lg">
      {[
        { v: true, label: "Yes" },
        { v: false, label: "No" },
      ].map((opt) => (
        <button
          key={opt.label}
          type="button"
          data-testid={`yesno-${testid}-${opt.label.toLowerCase()}`}
          onClick={() => onChange(opt.v)}
          className={`px-5 py-1.5 text-sm font-semibold rounded-md transition-all duration-200 ${
            value === opt.v
              ? "bg-white text-neutral-950 shadow-sm"
              : "text-neutral-500 hover:text-neutral-900"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  </div>
);

const Breakdown = ({ breakdown, state, money, rates }) => {
  const empty = !breakdown;
  const a = breakdown?.assumptions;
  const sets = breakdown?.server_sets ?? 0;
  const setsLabel = `${sets} set${sets === 1 ? "" : "s"}`;
  return (
    <div
      data-testid="pricing-breakdown"
      className="sticky top-24 bg-[#0A0A0A] text-white rounded-2xl p-6 sm:p-7 shadow-[0_30px_60px_-20px_rgba(10,10,10,0.4)] overflow-hidden relative"
    >
      <div
        aria-hidden
        className="absolute -top-24 -right-24 w-[280px] h-[280px] rounded-full blur-3xl opacity-30"
        style={{ background: "radial-gradient(circle, rgba(114,58,186,0.6), transparent 60%)" }}
      />
      <span className="font-mono-ui text-[10px] uppercase tracking-[0.22em] text-brand-accent">
        Your estimate
      </span>
      <div className="mt-4 space-y-3 relative">
        <Line
          label={`Monthly server${breakdown ? ` · ${breakdown.server_sets} set${breakdown.server_sets === 1 ? "" : "s"}` : ""}`}
          value={empty ? "-" : money(breakdown.monthly_server)}
          sub={a?.server_waived ? "Using your GPU servers - waived" : `${state.cameras} cam ÷ ${rates.setSize} = ${setsLabel} × ${money(rates.serverMonthly)} / mo`}
          testid="line-server"
        />
        <Line
          label={`Monthly features${state.features ? ` × ${state.features}` : ""}`}
          value={empty ? "-" : money(breakdown.monthly_features)}
          sub={state.features ? `${state.features} feature${state.features === 1 ? "" : "s"} × ${money(rates.featureMonthly)} / mo` : "-"}
          testid="line-features"
        />
        <hr className="border-white/10" />
        <Line
          label="Total / month"
          value={empty ? "-" : money(breakdown.monthly_total)}
          big
          testid="line-monthly-total"
        />
        <hr className="border-white/10" />
        <Line
          label="One-time install"
          value={empty ? "-" : money(breakdown.oneoff_cameras)}
          sub={a?.install_waived ? "Using your existing cameras - waived" : `${setsLabel} × ${money(rates.installPerSet)} one-off`}
          testid="line-install"
        />
        <hr className="border-white/10" />
        <div data-testid="line-first-month" className="bg-brand text-white -mx-2 px-3 py-3 rounded-lg flex items-end justify-between">
          <div>
            <div className="font-mono-ui text-[10px] uppercase tracking-[0.2em]">
              First-month total
            </div>
            <div className="text-[10px] font-mono-ui mt-0.5 opacity-80">
              Install (one-off) + month 1
            </div>
          </div>
          <div data-testid="value-first-month" className="font-display text-3xl font-black tracking-tight">
            {empty ? "-" : money(breakdown.first_month_total)}
          </div>
        </div>
      </div>
      <p className="mt-5 text-[11px] text-neutral-400 leading-relaxed">
        Indicative prices in {rates.currency}. Final pricing depends on site survey, network, integrations
        and SLA. Use the form below to share details and get a tailored quote.
      </p>
    </div>
  );
};

const Line = ({ label, value, sub, big, testid }) => (
  <div className="flex items-start justify-between gap-4" data-testid={testid}>
    <div>
      <div className={`font-display font-bold ${big ? "text-lg" : "text-sm"} text-white`}>
        {label}
      </div>
      {sub && (
        <div className="font-mono-ui text-[10px] uppercase tracking-[0.16em] text-neutral-400 mt-0.5">
          {sub}
        </div>
      )}
    </div>
    <div className={`${big ? "font-display text-2xl font-black tracking-tight text-brand-accent" : "font-display text-base font-bold text-white"}`}>
      {value}
    </div>
  </div>
);

const initialForm = {
  name: "",
  email: "",
  company: "",
  phone: "",
  message: "",
  custom_requirement: "",
};

const Inquiry = ({ state, sessionId, money }) => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and work email.");
      return;
    }
    setSubmitting(true);
    const summary = [
      `Cameras: ${state.cameras}`,
      `Features: ${state.features}`,
      `Existing cameras: ${state.has_existing_cameras ? "Yes" : "No"}`,
      `GPU servers: ${state.has_gpu_server ? "Yes" : "No"}`,
    ].join(" · ");
    const description = [form.custom_requirement, form.message, summary]
      .map((s) => (s || "").trim())
      .filter(Boolean)
      .join("\n");
    try {
      await submitBoth(
        axios.post(`${API}/pricing/inquiry`, {
          ...form,
          ...state,
          session_id: sessionId,
        }),
        submitSmartInquiry({
          name: form.name,
          work_email: form.email,
          company: form.company,
          phone: form.phone,
          primary_use_case: "Pricing quote",
          description,
        })
      );
      toast.success("Got it - our team will send a tailored quote within 1 business day.");
      setForm(initialForm);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="pricing-inquiry-section"
      className="mt-14 sm:mt-20 relative overflow-hidden rounded-2xl border border-neutral-200 bg-white"
    >
      <div className="grid lg:grid-cols-12">
        <div className="lg:col-span-5 bg-[#0A0A0A] text-white p-7 sm:p-10 relative overflow-hidden">
          <div
            aria-hidden
            className="absolute -bottom-32 -right-24 w-[420px] h-[420px] rounded-full blur-3xl opacity-30"
            style={{ background: "radial-gradient(circle, rgba(245,158,11,0.55), transparent 60%)" }}
          />
          <span className="font-mono-ui text-[10px] uppercase tracking-[0.22em] text-brand-accent relative">
            Custom requirement?
          </span>
          <h2 className="relative font-display mt-2 text-2xl sm:text-3xl font-black tracking-tight leading-tight">
            Do reach out to us.
          </h2>
          <p className="relative mt-4 text-sm text-neutral-300 leading-relaxed max-w-sm">
            Multi-site rollouts, on-prem hardware deals, integrations with your existing VMS,
            multi-year terms - we tailor everything. Share what you need below and we&apos;ll come
            back with a written proposal.
          </p>
          <div className="relative mt-6 grid grid-cols-2 gap-3">
            <Mini label="Cameras" value={state.cameras} />
            <Mini label="Features" value={state.features} />
            <Mini label="Existing cameras" value={state.has_existing_cameras ? "Yes" : "No"} />
            <Mini label="GPU servers" value={state.has_gpu_server ? "Yes" : "No"} />
          </div>
        </div>

        <form onSubmit={submit} data-testid="pricing-inquiry-form" className="lg:col-span-7 p-7 sm:p-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Full name *">
              <Input
                data-testid="inquiry-input-name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Ayesha Rahman"
              />
            </Field>
            <Field label="Work email *">
              <Input
                data-testid="inquiry-input-email"
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="ayesha@company.com"
              />
            </Field>
            <Field label="Company">
              <Input
                data-testid="inquiry-input-company"
                value={form.company}
                onChange={(e) => update("company", e.target.value)}
                placeholder="Your company"
              />
            </Field>
            <Field label="Phone">
              <Input
                data-testid="inquiry-input-phone"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="+971 5X XXX XXXX"
              />
            </Field>
            <Field label="Custom requirement" full>
              <Textarea
                data-testid="inquiry-input-custom"
                value={form.custom_requirement}
                onChange={(e) => update("custom_requirement", e.target.value)}
                rows={3}
                placeholder="Anything not covered by the calculator - VMS integration, special SLAs, multi-site, etc."
              />
            </Field>
            <Field label="Anything else" full>
              <Textarea
                data-testid="inquiry-input-message"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                rows={2}
                placeholder="Timeline, budget range, points of contact..."
              />
            </Field>
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
            <p className="text-xs text-neutral-500 max-w-sm">
              By submitting, you agree to be contacted by {BRAND_NAME} about this enquiry.
            </p>
            <Button
              type="submit"
              data-testid="inquiry-submit"
              disabled={submitting}
              className="bg-brand text-white hover:bg-brand-dark font-semibold px-6 py-3 h-auto"
            >
              {submitting ? "Sending..." : "Get a tailored quote"}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
};

const Mini = ({ label, value }) => (
  <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-2">
    <div className="font-mono-ui text-[9px] uppercase tracking-[0.2em] text-neutral-400">
      {label}
    </div>
    <div className="font-display font-bold text-white text-lg mt-0.5">{value}</div>
  </div>
);

const Field = ({ label, children, full }) => (
  <label className={`block ${full ? "md:col-span-2" : ""}`}>
    <span className="block font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1.5">
      {label}
    </span>
    {children}
  </label>
);
